import React, { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Bot, User, Loader } from "lucide-react";
import { callOpenAI } from "../../Services/openai-service";
import { useEvents } from "../../Contexts/EventsContext";

const buildEventsContext = (events) => {
  if (!events || events.length === 0) return "";
  const eventsInfo = events
    .map(
      (e) =>
        `- "${e.title}" (${e.eventType || "event"}): ${e.description || ""}. Date: ${e.date || "TBA"}. Location: ${e.location || "TBA"}. Deadline: ${e.deadline || "TBA"}. Prizes: ${e.prizes || "N/A"}.`,
    )
    .join("\n");
  return eventsInfo;
};

const LANDING_INSTRUCTIONS_BASE = `You are a friendly AI assistant for the STI Events Platform — Uganda's hub for hackathons, innovation challenges, and technology events organized by the Science, Technology and Innovation Secretariat (STI).

Your role:
- Help visitors understand what events are available and how to participate.
- Answer questions about event details, deadlines, registration, and requirements.
- Keep answers concise, friendly and helpful. Use bullet points when listing info.
- If you don't know something, say so and suggest the user visit the event page for full details.
- Do NOT make up information. Only use what's provided in the context below.

How the platform works:
- Users browse events on the homepage and click "Register Now" or "View Details."
- To participate in a hackathon, users must create an account, then join or create a team.
- Teams typically need 2-6 members. Once on a team, members can submit applications together.
- After registration closes, teams work on their projects with mentor support.
- The platform is run by the National Research and Innovation Program (NRIP) under STI.

Current events on the platform:
`;

const EventsChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { events } = useEvents();
  const [messages, setMessages] = useState([
    {
      id: 1,
      role: "assistant",
      content:
        "Hi there! 👋 I'm your STI Events assistant. Ask me anything about our events, registration, or how to get started!",
      timestamp: new Date(),
    },
  ]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [isOpen]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || isLoading) return;

    const userMsg = {
      id: Date.now(),
      role: "user",
      content: inputMessage.trim(),
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMsg]);
    setInputMessage("");
    setIsLoading(true);

    try {
      const today = new Date().toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
      const instructions =
        LANDING_INSTRUCTIONS_BASE +
        `\nToday's date is: ${today}. Use this to determine whether deadlines have passed or events are still open.\n\n` +
        buildEventsContext(events);
      const response = await callOpenAI(inputMessage.trim(), instructions);
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          role: "assistant",
          content: response,
          timestamp: new Date(),
        },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          role: "assistant",
          content: "Sorry, something went wrong. Please try again.",
          timestamp: new Date(),
          isError: true,
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (date) =>
    date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

  return (
    <>
      {/* Chat Panel */}
      {isOpen && (
        <div className="fixed bottom-20 right-4 sm:right-6 z-50 w-[340px] sm:w-[380px] h-[480px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200 animate-in">
          {/* Header */}
          <div className="bg-custom-blue-1 text-white px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <Bot className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-sm font-bold leading-tight">
                  STI Assistant
                </h3>
                <p className="text-[10px] text-white/70">
                  Ask about events & registration
                </p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-gray-50">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex gap-2 ${
                  msg.role === "user" ? "flex-row-reverse" : "flex-row"
                }`}
              >
                <div
                  className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                    msg.role === "user"
                      ? "bg-custom-blue-1 text-white"
                      : "bg-custom-green-1 text-white"
                  }`}
                >
                  {msg.role === "user" ? (
                    <User className="w-3.5 h-3.5" />
                  ) : (
                    <Bot className="w-3.5 h-3.5" />
                  )}
                </div>
                <div
                  className={`flex flex-col max-w-[75%] ${
                    msg.role === "user" ? "items-end" : "items-start"
                  }`}
                >
                  <div
                    className={`px-3 py-2 rounded-xl text-sm ${
                      msg.role === "user"
                        ? "bg-custom-blue-1 text-white rounded-tr-none"
                        : msg.isError
                          ? "bg-red-100 text-red-700 rounded-tl-none"
                          : "bg-white text-gray-800 shadow-sm rounded-tl-none"
                    }`}
                  >
                    <p className="whitespace-pre-wrap break-words text-[13px] leading-relaxed">
                      {msg.content}
                    </p>
                  </div>
                  <span className="text-[10px] text-gray-400 mt-0.5 px-1">
                    {formatTime(msg.timestamp)}
                  </span>
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="flex gap-2">
                <div className="w-6 h-6 rounded-full bg-custom-green-1 text-white flex items-center justify-center flex-shrink-0">
                  <Bot className="w-3.5 h-3.5" />
                </div>
                <div className="bg-white px-3 py-2 rounded-xl rounded-tl-none shadow-sm">
                  <div className="flex items-center gap-1.5">
                    <Loader className="w-3.5 h-3.5 animate-spin text-custom-blue-1" />
                    <span className="text-xs text-gray-500">Thinking...</span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="border-t border-gray-200 p-3 bg-white flex-shrink-0">
            <form onSubmit={handleSend} className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSend(e);
                  }
                }}
                placeholder="Ask about events..."
                className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-custom-blue-1"
                disabled={isLoading}
              />
              <button
                type="submit"
                disabled={!inputMessage.trim() || isLoading}
                className="p-2 bg-custom-green-1 text-white rounded-lg hover:bg-custom-green-1/90 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <Loader className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Floating Action Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-5 right-4 sm:right-6 z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 ${
          isOpen
            ? "bg-gray-600 hover:bg-gray-700 rotate-0"
            : "bg-custom-green-1 hover:bg-custom-green-1/90 animate-bounce-slow"
        }`}
        aria-label={isOpen ? "Close chat" : "Open chat"}
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <MessageCircle className="w-6 h-6 text-white" />
        )}
      </button>
    </>
  );
};

export default EventsChatWidget;
